import { useMemo } from "react";
import moment from "moment-timezone";
import { useCalendar } from "../../../../context/CalendarContext";
import { useSalon } from "../../../../context/SalonContext";
import type { Employee } from "../../../../types/salon";
import { filterBookableActiveStaff } from "../../../../utils/staffUtils";
import { useTypedCalendarEvent } from "../calendarViewTypes";
import { staffAvailability, type StaffAvailability } from "./staffAvailability";
import { useMinuteNow } from "./useMinuteNow";

export interface StaffAvailabilityNow {
  /** Every bookable provider, available first. */
  rows: StaffAvailability[];
  /** Epoch ms (ticking). */
  nowMs: number;
  /** The same instant as a salon wall-clock fake-local Date. */
  nowWall: Date;
}

/**
 * Who is free right now, from what the calendar already loaded (the event
 * store's appointments + time blocks, the staff weekly hours and
 * CalendarContext's hours overrides). Ticks every 30s while mounted (the
 * walk-ins drawer / sheet).
 */
export function useStaffAvailability(timezone: string): StaffAvailabilityNow {
  const nowMs = useMinuteNow();
  const { events } = useTypedCalendarEvent();
  const { hoursOverrides } = useCalendar();
  const { employees } = useSalon() as { employees: Employee[] | undefined };

  const staff = useMemo(
    () => filterBookableActiveStaff(employees || []) as Employee[],
    [employees],
  );
  const nowWall = useMemo(
    () => new Date(moment(nowMs).tz(timezone).format("YYYY-MM-DDTHH:mm:ss")),
    [nowMs, timezone],
  );
  const rows = useMemo(
    () =>
      staffAvailability({
        staff,
        events: events || [],
        now: nowWall,
        overrides: hoursOverrides || [],
      }),
    [staff, events, nowWall, hoursOverrides],
  );
  return { rows, nowMs, nowWall };
}
